import { useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Input } from '@/components/ui/shadcn/input'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  current: string
  onSelect: (timezone: string) => void
  isSaving?: boolean
}

const TIMEZONES: string[] = (() => {
  type IntlWithSupportedValues = typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[]
  }
  const intl = Intl as IntlWithSupportedValues
  if (typeof intl.supportedValuesOf === 'function') {
    return intl.supportedValuesOf('timeZone')
  }
  // Fallback for older runtimes — small curated list. The picker still works,
  // just with fewer options. Modern browsers all support the API.
  return ['UTC', 'Europe/Berlin', 'Europe/London', 'America/New_York', 'America/Los_Angeles']
})()

export function TimezonePickerDialog({ open, onOpenChange, current, onSelect, isSaving }: Props) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return TIMEZONES
    return TIMEZONES.filter((tz) => tz.toLowerCase().includes(q))
  }, [query])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-border border-b p-4">
          <DialogTitle>Brand timezone</DialogTitle>
        </DialogHeader>
        <div className="border-border border-b p-3">
          <Input
            autoFocus
            placeholder="Search timezones…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="text-muted-foreground p-4 text-sm">No matches.</p>
          ) : (
            filtered.map((tz) => {
              const isCurrent = tz === current
              return (
                <button
                  key={tz}
                  type="button"
                  disabled={isSaving}
                  onClick={() => onSelect(tz)}
                  className={cn(
                    'hover:bg-hover flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors',
                    isCurrent && 'bg-hover font-medium',
                  )}>
                  <span>{tz}</span>
                  {isCurrent && <Check className="h-4 w-4" />}
                </button>
              )
            })
          )}
        </div>
        <DialogFooter className="border-border gap-2 border-t p-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
